// src/modules/app-views/account/pages/Init/modals/TeamInvitationModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import SimpleEmail from 'components/SimpleComponents/SimpleEmail';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import { API } from 'constants/Api';

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const EmailStepContent = () => (
  <SimpleEmail
    name="email"
    required
    rules={[{ type: 'email', message: translateX('invalid_email') }]}
  />
);

// *******************************************************************************
// HELPERS
// *******************************************************************************
const checkEmailInTeam = (email, storedTeam) => {
  const normalizedEmail = (email || '').trim().toLowerCase();
  return (storedTeam || []).find(
    member => (member?.email?.value || member?.email || '').toLowerCase() === normalizedEmail
  );
};

const checkUserInDatabase = async email => {
  try {
    const [doc] = await axiosService.get({
      url: mergeUrlParamsQuery({
        url: API.INVITATIONS.GET_USER_BY_EMAIL,
        params: { email },
      }),
      returnAsObject: true,
    });
    // API returns: { account, invitation, collaborator }

    return doc || { account: null, invitation: null, collaborator: null };
  } catch {
    return { account: null, invitation: null, collaborator: null };
  }
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const emailStep = ({
  storedTeam,
  setFoundAccount,
  setFoundCollaborator,
  setFoundInvitation,
  resetFoundStates,
}) => ({
  stepTitle: 'email',
  stepTooltip: 'inform_an_email',
  content: <EmailStepContent />,
  validationFields: ['email'],
  onNext: async ({ form, goFocus }) => {
    const email = (form?.getFieldValue('email') || '').trim().toLowerCase();

    // Check if email already exists in team
    const existsInTeam = checkEmailInTeam(email, storedTeam);

    if (existsInTeam) {
      const translate =
        existsInTeam?.profile === 'owner'
          ? 'account_email_owner_found'
          : 'account_email_guest_found';
      const userName = existsInTeam?.name || existsInTeam?.fullName || email;
      await appService.message({
        flag: 'w',
        text: translateX(translate, { name: userName }),
        key: 'teamAddError',
        duration: 4,
      });
      setTimeout(() => goFocus?.('email', true), 0);
      return false; // Don't advance
    }

    // Reset found states before new search
    resetFoundStates();

    // Check if user exists in database (account, invitation, or collaborator)
    const { account, invitation, collaborator } = await checkUserInDatabase(email);

    // Store found data in parent state
    setFoundAccount(account);
    setFoundCollaborator(collaborator);
    setFoundInvitation(invitation);

    // Set the name field: account.name || collaborator.name || invitation.name
    const foundName = account?.name || collaborator?.name || ''; //|| invitation?.name || '';
    form?.setFieldValue('name', foundName);

    // Profile is subscription-specific: do NOT pre-fill from account/invitation
    // found in other subscriptions. Admin must pick the profile for THIS subscription.
    form?.setFieldValue('profile', undefined);

    return true; // Advance to next step
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default emailStep;
